import {inject, injectable} from "inversify";
import {ListPaginationEntity} from "@dropDesk/domain/entities/common/list_pagination.entity";
import {InternalChatRepository} from "@dropDesk/domain/repositories/internal_chat/internal_chat.repository";
import {InternalChatRemoteDataSource} from "@dropDesk/data/data_source/internal_chat/internal_chat_remote_datasource";
import {InternalChatEntity} from "@dropDesk/domain/entities/internal_chat/internal_chat_entity";
import {InternalChatMessageEntity} from "@dropDesk/domain/entities/internal_chat/messages/internal_chat_message_entity";
import {InternalChatListFilters} from "@dropDesk/domain/entities/internal_chat/internal_chat_list_filters";
import {InternalChatUsersListFilters} from "@dropDesk/domain/entities/internal_chat/internal_chat_users_list_filters";
import {InternalChatUserEntity} from "@dropDesk/domain/entities/internal_chat/users/internal_chat_user_entity";
import {ServerError} from "@dropDesk/domain/entities/exceptions/exceptions";
import {Observable} from "rxjs";
import {CancelTokenSource} from "axios";
import {UserEntity} from "@dropDesk/domain/entities/user/user.entity";

@injectable()
export class InternalChatRepositoryImpl implements InternalChatRepository {
    private _dataSource: InternalChatRemoteDataSource;

    constructor(@inject(InternalChatRemoteDataSource) dataSource: InternalChatRemoteDataSource) {
        this._dataSource = dataSource;
    }

    public listInternalChats(filters: InternalChatListFilters): Promise<ListPaginationEntity<InternalChatEntity>> {
        try {
            return this._dataSource.listInternalChats(filters);
        } catch (e: any) {
            throw new ServerError(e);
        }
    }

    public subscribe(): Observable<InternalChatEntity[]> {
        try {
            return this._dataSource.subscribe();
        } catch (e: any) {
            throw new ServerError(e);
        }
    }

    public loadMessages(internalChatId: string, page: number, cancelTokenSource?: CancelTokenSource): Promise<ListPaginationEntity<InternalChatMessageEntity>> {
        try {
            return this._dataSource.loadMessages(internalChatId, page, cancelTokenSource);
        } catch (e: any) {
            throw new ServerError(e);
        }
    }

    public sendMessage(message: InternalChatMessageEntity): Promise<InternalChatMessageEntity> {
        try {
            return this._dataSource.sendMessage(message);
        } catch (e: any) {
            throw new ServerError(e);
        }
    }

    public deleteMessage(id: string): Promise<void> {
        try {
            return this._dataSource.deleteMessage(id);
        } catch (e: any) {
            throw new ServerError(e);
        }
    }

    public editMessage(idMessage: string, text: string): Promise<void> {
        try {
            return this._dataSource.editMessage(idMessage, text);
        } catch (e: any) {
            throw new ServerError(e);
        }
    }

    public informReadMessages(internalChatId: string, cancelTokenSource?: CancelTokenSource): Promise<void> {
        try {
            return this._dataSource.informReadMessages(internalChatId, cancelTokenSource);
        } catch (e: any) {
            throw new ServerError(e);
        }
    }

    public clearInternalChatsCache(): void {
        try {
            return this._dataSource.clearInternalChatsCache();
        } catch (e: any) {
            throw new ServerError(e);
        }
    }

    public getInternalChatFromCache(id: string): InternalChatEntity | undefined {
        try {
            return this._dataSource.getInternalChatFromCache(id);
        } catch (e: any) {
            throw new ServerError(e);
        }
    }

    public updateCacheFromSingleInternalChat(internalChat: InternalChatEntity): void {
        try {
            return this._dataSource.updateCacheFromSingleInternalChat(internalChat);
        } catch (e: any) {
            throw new ServerError(e);
        }
    }

    public set(internalChat: InternalChatEntity): Promise<InternalChatEntity> {
        try {
            return this._dataSource.set(internalChat);
        } catch (e: any) {
            throw new ServerError(e);
        }
    }

    public changeDescription(id: string, description: string): Promise<InternalChatEntity> {
        try {
            return this._dataSource.changeDescription(id, description);
        } catch (e: any) {
            throw new ServerError(e);
        }
    }

    public delete(id: string): Promise<void> {
        try {
            return this._dataSource.delete(id);
        } catch (e: any) {
            throw new ServerError(e);
        }
    }

    public listUsers(filters: InternalChatUsersListFilters): Promise<ListPaginationEntity<InternalChatUserEntity>> {
        try {
            return this._dataSource.listUsers(filters);
        } catch (e: any) {
            throw new ServerError(e);
        }
    }

    public changeUsers(id: string, users: InternalChatUserEntity[]): Promise<InternalChatEntity> {
        try {
            return this._dataSource.changeUsers(id, users);
        } catch (e: any) {
            throw new ServerError(e);
        }
    }

    public subscriptionUploadFile(message: InternalChatMessageEntity): Observable<InternalChatMessageEntity> {
        try {
            return this._dataSource.subscriptionUploadFile(message);
        } catch (e: any) {
            throw new ServerError(e);
        }
    }

    public navigateToMessage(id: string, idInternalChat: string): Promise<ListPaginationEntity<InternalChatMessageEntity>> {
        try {
            return this._dataSource.navigateToMessage(id, idInternalChat);
        } catch (e: any) {
            throw new ServerError(e.message);
        }
    }

    public reactMessage(messageId: string, reactionId: string, reaction: string): Promise<void> {
        try {
            return this._dataSource.reactMessage(messageId, reactionId, reaction);
        } catch (e: any) {
            throw new ServerError(e.message);
        }
    }

    public getUsersFromCache(ids: string[]): Promise<UserEntity[]> {
        try {
            return this._dataSource.getUsersFromCache(ids);
        } catch (e: any) {
            throw new ServerError(e.message);
        }
    }

    public getMessageById(id: string): Promise<InternalChatMessageEntity> {
        try {
            return this._dataSource.getMessageById(id);
        } catch (e: any) {
            throw new ServerError(e.message);
        }
    }
}
